import {styled} from '@mui/material/styles'
import Box from '@mui/material/Box'
import FormControlLabel from '@mui/material/FormControlLabel'
import Chip from '@mui/material/Chip'
import OutlinedInput from '@mui/material/OutlinedInput'
import Close from '@mui/icons-material/Close'

/**
 * Container - Main wrapper for CheckboxGroup
 */
export const Container = styled(Box)({
  width: '100%'
})

/**
 * StyledOutlinedInput - Input field with white background and cursor pointer
 */
export const StyledOutlinedInput = styled(OutlinedInput)(({theme}) => ({
  backgroundColor: theme.palette.hpWhite,
  fontStyle: 'italic',
  cursor: 'pointer',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.hpBaseLight
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.hpPrimary
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.hpPrimary
  },
  '& input': {
    backgroundColor: theme.palette.hpWhite,
    padding: '8px 12px',
    cursor: 'pointer'
  },
  '& input::placeholder': {
    opacity: 1,
    color: theme.palette.text.secondary
  }
}))

/**
 * StyledFormControlLabel - Form control label with hover effect
 */
export const StyledFormControlLabel = styled(FormControlLabel)(({theme}) => ({
  margin: 0,
  fontSize: theme.typography.h6.fontSize,
  '&:hover': {
    backgroundColor: theme.palette.hpBaseLighter
  },
  '& .MuiFormControlLabel-label': {
    fontSize: theme.typography.subtitle1.fontSize,
    fontWeight: theme.typography.subtitle2.fontWeight,
    color: theme.palette.hpBlack
  }
}))

/**
 * StyledChip - Chip component with white background
 */
export const StyledChip = styled(Chip)(({theme}) => ({
  backgroundColor: theme.palette.hpWhite,
  color: theme.palette.hpAccentBlack,
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: theme.typography.body2.fontSize,
  height: theme.spacing(3),
  borderRadius: theme.spacing(0.5),
  '& .MuiChip-label': {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(0.5),
    color: theme.palette.hpAccentBlack
  },
  '& .MuiIconButton-root': {
    padding: 0
  },
  '& .MuiChip-deleteIcon': {
    color: theme.palette.hpBaseDark,
    fontSize: theme.typography.body2.fontSize,
    margin: `0 ${theme.spacing(0.5)} 0 0`,
    '&:hover': {
      color: theme.palette.hpAccentBlack
    }
  },
  '&:hover': {
    backgroundColor: theme.palette.hpBaseLighter,
    borderColor: theme.palette.grey[200]
  }
}))

/**
 * ChipContainer - Container for selected value chips
 */
export const StyledChipContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1)
}))

/**
 * PopoverContent - Container for popover content
 */
export const PopoverContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  paddingLeft: 8,
  paddingRight: 8
})

/**
 * StyledCloseIcon - Close icon for chips
 */
export const StyledCloseIcon = styled(Close)(({theme}) => ({
  fontSize: theme.typography.subtitle1.fontSize
}))

/**
 * StyledSearchInput - Search input field for filtering options
 */
export const StyledSearchInput = styled(OutlinedInput)(({theme}) => ({
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.divider
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
    borderWidth: '2px'
  }
}))

/**
 * NoResultsMessage - Message displayed when search returns no results
 */
export const NoResultsMessage = styled(Box)(({theme}) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  fontStyle: 'italic'
}))

/**
 * getPopoverPaperStyles - Returns paper styles for Popover
 */
export const getPopoverPaperStyles = (width, maxHeight, theme) => ({
  width,
  maxHeight,
  border: `1px solid ${theme.palette.hpBaseLight}`,
  py: 1
})
